import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

@Component({
    selector: 'app-right-sidebar',
    templateUrl: './right_sidebar.component.html',
    styleUrls: ['./right_sidebar.component.css'],
})
export class RightSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() Username!: string;
    @Input() UserID!: number;

    @ViewChild('searchBand', { static: false })
    searchBandRef!: ElementRef<HTMLElement>;

    @ViewChild('widgetsViewport', { static: false })
    widgetsViewportRef!: ElementRef<HTMLElement>;

    @ViewChild('widgetsInner', { static: false })
    widgetsInnerRef!: ElementRef<HTMLElement>;

    showWhoToFollow = true;
    showTrendingTopics = true;
    showSponsoredPosts = true;

    searchReservePx = 56;
    private readonly railGapPx = 16;

    private resizeObserver?: ResizeObserver;
    private resizeRafId = 0;

    private lastScrollY = 0;
    private sidebarScrollOffset = 0;
    private maxScrollable = 0;
    private viewportHeight = 0;
    private widgetsInnerHeight = 0;

    private boundScrollHandler!: () => void;

    constructor(
        private hostRef: ElementRef<HTMLElement>,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
    ) { }

    ngOnInit(): void { }

    ngAfterViewInit(): void {
        this.lastScrollY = this.getScrollY();
        this.updateLayoutMetrics();
        this.applySidebarTransform();

        this.ngZone.runOutsideAngular(() => {
            this.boundScrollHandler = () => this.onScrollOutsideZone();
            window.addEventListener('scroll', this.boundScrollHandler, { passive: true });

            this.resizeObserver = new ResizeObserver(() => {
                this.queueMeasureAndApply();
            });

            const host = this.hostRef.nativeElement;
            const searchEl = this.searchBandRef?.nativeElement;
            const viewportEl = this.widgetsViewportRef?.nativeElement;
            const innerEl = this.widgetsInnerRef?.nativeElement;

            if (host) this.resizeObserver.observe(host);
            if (searchEl) this.resizeObserver.observe(searchEl);
            if (viewportEl) this.resizeObserver.observe(viewportEl);
            if (innerEl) this.resizeObserver.observe(innerEl);
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('scroll', this.boundScrollHandler);
        this.resizeObserver?.disconnect();
        if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.queueMeasureAndApply();
    }

    toggleWidgetVisibility(widget: string, hasData: boolean): void {
        switch (widget) {
            case 'whoToFollow': this.showWhoToFollow = hasData; break;
            case 'trendingTopics': this.showTrendingTopics = hasData; break;
            case 'sponsoredPosts': this.showSponsoredPosts = hasData; break;
        }

        queueMicrotask(() => {
            this.queueMeasureAndApply();
            this.cdr.markForCheck();
        });
    }

    navigateToProfile(): void {
        this.router.navigate(['/', this.Username]);
    }

    isProfileActive(): boolean {
        return this.router.url === `/${this.Username}`;
    }

    refreshPosts(): void { }

    private onScrollOutsideZone(): void {
        this.handleScroll();
    }

    private handleScroll(): void {
        const currentY = this.getScrollY();
        const delta = currentY - this.lastScrollY;
        this.lastScrollY = currentY;

        if (this.maxScrollable <= 0) {
            if (this.sidebarScrollOffset !== 0) {
                this.sidebarScrollOffset = 0;
                this.applySidebarTransform();
            }
            return;
        }

        const next = this.clamp(this.sidebarScrollOffset + delta, 0, this.maxScrollable);
        if (next !== this.sidebarScrollOffset) {
            this.sidebarScrollOffset = next;
            this.applySidebarTransform();
        }
    }

    private queueMeasureAndApply(): void {
        if (this.resizeRafId) {
            cancelAnimationFrame(this.resizeRafId);
        }
        this.resizeRafId = requestAnimationFrame(() => {
            this.resizeRafId = 0;
            this.updateLayoutMetrics();
            this.clampSidebarOffset();
            this.applySidebarTransform();
            this.ngZone.run(() => this.cdr.markForCheck());
        });
    }

    private updateLayoutMetrics(): void {
        const host = this.hostRef.nativeElement;
        const searchEl = this.searchBandRef?.nativeElement;
        const viewportEl = this.widgetsViewportRef?.nativeElement;
        const innerEl = this.widgetsInnerRef?.nativeElement;

        if (!host || !searchEl || !viewportEl || !innerEl) return;

        const searchHeight = Math.ceil(searchEl.getBoundingClientRect().height);
        if (searchHeight >= 20) {
            this.searchReservePx = searchHeight;
        }

        const topOffset = this.searchReservePx + this.railGapPx;
        host.style.setProperty('--ts-right-search-height', `${this.searchReservePx}px`);
        host.style.setProperty('--ts-right-widgets-top', `${topOffset}px`);

        const viewportRect = viewportEl.getBoundingClientRect();
        const innerRect = innerEl.getBoundingClientRect();

        this.viewportHeight = Math.max(0, Math.floor(viewportRect.height));
        this.widgetsInnerHeight = Math.max(0, Math.ceil(innerEl.scrollHeight || innerRect.height));
        this.maxScrollable = Math.max(0, this.widgetsInnerHeight - this.viewportHeight);
    }

    private clampSidebarOffset(): void {
        this.sidebarScrollOffset = this.clamp(this.sidebarScrollOffset, 0, this.maxScrollable);
    }

    private applySidebarTransform(): void {
        const innerEl = this.widgetsInnerRef?.nativeElement;
        if (!innerEl) return;
        innerEl.style.transform = `translate3d(0, ${-this.sidebarScrollOffset}px, 0)`;
    }

    /** Same vertical scroll position the browser uses for the main page (matches infinite-scroll logic). */
    private getScrollY(): number {
        const se = document.scrollingElement;
        if (se) {
            return se.scrollTop;
        }
        return (
            window.scrollY ??
            window.pageYOffset ??
            document.documentElement.scrollTop ??
            0
        );
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    }
}
